import React from 'react';
import { motion } from 'framer-motion';
import { Fade } from 'react-awesome-reveal';

// Valeurs avec icônes SVG professionnelles (Gris/Doré)
const values = [
  {
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#C1A68D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3v18"/><path d="M3 7h18"/><path d="M6 7v6a6 6 0 0 0 12 0V7"/>
      </svg>
    ),
    title: 'Justice',
    text: 'Nous plaçons la justice au cœur de chaque action menée avec une détermination absolue.',
  },
  {
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#C1A68D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
    title: 'Intégrité',
    text: 'Nous agissons toujours avec honnêteté, transparence et dans le strict respect de la déontologie.',
  },
  {
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#C1A68D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/>
      </svg>
    ),
    title: 'Expertise',
    text: 'Une équipe hautement qualifiée et spécialisée pour des conseils juridiques avisés.',
  },
  {
    icon: (
      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#C1A68D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="7" width="20" height="14" rx="2" ry="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/>
      </svg>
    ),
    title: 'Excellence',
    text: 'Un accompagnement rigoureux et personnalisé pour assurer la réussite de chaque client.',
  },
];

const AboutSection0 = () => {
  const galleryImages = [
    '/images/L1.webp',
    '/images/L2.png',
    '/images/L3.webp',
    '/images/L4.jpg',
  ];

  return (
    <section className="about-section">
      {/* Noise Overlay (Texture luxueuse) */}
      <div className="noise-overlay" />

      {/* Hero Section */}
      <div className="about-hero">
        <div className="hero-content">
          <Fade direction="left" cascade triggerOnce>
            <div className="hero-text-container">
              <motion.div 
                className="title-container"
                whileHover={{ x: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <h1 className="main-title">
                  <span className="title-gradient">Notre Histoire</span>
                  <motion.span 
                    className="subtitle"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.4 }}
                  >
                    3 ans d'expertise
                  </motion.span>
                </h1>
              </motion.div>
              
              <div className="text-content">
                <p className="hero-text">
                  Fort de plus de sept années d'expérience collective dans le domaine juridique,
                  notre cabinet s'engage à fournir des services juridiques de haute qualité, alliant rigueur, discrétion et excellence.
                </p>
                <p className="hero-text">
                  Nous intervenons dans l'assistance, le conseil stratégique et la représentation de nos clients sur des problématiques juridiques complexes. 
                  Spécialisés en droit des affaires, nous disposons d'une expertise approfondie en droit commercial, droit des sociétés, contentieux économique et droit contractuel.
                </p>
              </div>
            </div>
          </Fade>

          <Fade direction="right" triggerOnce>
            <div className="hero-image">
              <motion.div 
                className="image-wrapper"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.5 }}
              >
                <img
                  src="/images/pic/group.jpeg"
                  alt="Notre histoire"
                  className="image"
                  loading="lazy"
                />
                <motion.div
                  className="floating-badge"
                  animate={{ y: [0, -10, 0] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                >
                  <span className="badge-text">Depuis 2024</span>
                  <div className="badge-glow" />
                </motion.div>
                <div className="image-overlay-gradient" />
              </motion.div>
            </div>
          </Fade>
        </div>

        {/* Stats Section */}
        <div className="stats-container">
          <div className="stats-grid">
            <motion.div className="stat-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} viewport={{ once: true }}>
              <div className="stat-number">2+</div>
              <div className="stat-label">Années d'expérience</div>
            </motion.div>
            
            <motion.div className="stat-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} viewport={{ once: true }}>
              <div className="stat-number">200+</div>
              <div className="stat-label">Dossiers traités</div>
            </motion.div>
            
            <motion.div className="stat-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} viewport={{ once: true }}>
              <div className="stat-number">98%</div>
              <div className="stat-label">Taux de réussite</div>
            </motion.div>
            
            <motion.div className="stat-card" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} viewport={{ once: true }}>
              <div className="stat-number">24/7</div>
              <div className="stat-label">Disponibilité</div>
            </motion.div>
          </div>
        </div>

        {/* Image Grid */}
        <div className="gallery-grid">
          {galleryImages.map((img, index) => (
            <motion.div
              key={index}
              className="gallery-item"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "0px 0px -100px 0px" }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -5, transition: { duration: 0.3 } }}
            >
              <div className="gallery-image-wrapper">
                <img src={img} alt={`Expertise KMJ ${index + 1}`} className="gallery-image" loading="lazy" />
                <div className="gallery-gradient" />
                <div className="gallery-overlay">
                  <h3 className="gallery-title">Expertise Juridique</h3>
                  <div className="gallery-line" />
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Values Section */}
      <div className="values-section">
        <motion.div 
          className="section-header"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="section-title">
            <span className="title-gradient">Nos Valeurs</span>
          </h2>
          <p className="section-subtitle">
            Les principes fondamentaux qui guident chaque action de notre cabinet
          </p>
        </motion.div>

        <div className="values-grid">
          {values.map((value, index) => (
            <motion.div
              key={index}
              className="value-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true, margin: "0px 0px -50px 0px" }}
              whileHover={{ 
                y: -5,
                borderColor: '#C1A68D',
                boxShadow: '0 15px 30px rgba(0, 0, 0, 0.4)'
              }}
            >
              <div className="value-icon-wrapper">
                <span className="value-icon">{value.icon}</span>
                <div className="icon-glow" />
              </div>
              <h3 className="value-title">{value.title}</h3>
              <p className="value-text">{value.text}</p>
            </motion.div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .about-section {
          background: linear-gradient(135deg, #0A0A0A 0%, #161616 50%, #050505 100%);
          color: #F5F0E6;
          padding: 6rem 2rem;
          overflow: hidden;
          position: relative;
        }

        .noise-overlay {
          position: absolute;
          inset: 0;
          background-image: url('https://www.transparenttextures.com/patterns/stardust.png');
          opacity: 0.15;
          mix-blend-mode: overlay;
          pointer-events: none;
          z-index: 0;
        }

        .about-hero {
          max-width: 1600px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .hero-content {
          display: grid;
          grid-template-columns: 0.7fr 0.9fr;
          gap: 5rem;
          align-items: center;
          margin-bottom: 6rem;
        }

        @media (max-width: 1024px) {
          .hero-content {
            gap: 3rem;
          }
        }

        @media (max-width: 768px) {
          .hero-content {
            grid-template-columns: 1fr;
            gap: 4rem;
          }
        }

        .hero-text-container {
          position: relative;
        }

        .title-container {
          margin-bottom: 2.5rem;
          border-left: 3px solid #C1A68D;
          padding-left: 1.5rem;
        }

        .main-title {
          margin: 0;
          line-height: 1.2;
        }

        .title-gradient {
          color: #F5F0E6;
          font-size: 3.5rem;
          font-weight: 700;
          display: block;
          margin-bottom: 0.5rem;
          text-transform: uppercase;
          letter-spacing: 2px;
        }

        .subtitle {
          font-size: 1.4rem;
          color: #C1A68D;
          display: block;
          font-weight: 400;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .text-content {
          max-width: 650px;
        }

        .hero-text {
          font-size: 1.1rem;
          line-height: 1.8;
          color: #D4C5B0;
          margin-bottom: 1.5rem;
          font-weight: 300;
        }

        @media (max-width: 768px) {
          .title-gradient { font-size: 2.5rem; }
          .subtitle { font-size: 1.2rem; }
          .hero-text { font-size: 1rem; }
        }

        /* Hero Image */
        .hero-image {
          position: relative;
          border-radius: 4px;
          padding: 1rem;
          background: rgba(255, 255, 255, 0.02);
          border: 1px solid rgba(193, 166, 141, 0.1);
        }

        .image-wrapper {
          position: relative;
          height: 600px;
          border-radius: 4px;
          overflow: hidden;
        }

        .image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.8s ease;
        }

        .image-wrapper:hover .image {
          transform: scale(1.03);
        }

        .image-overlay-gradient {
          position: absolute;
          inset: 0;
          background: linear-gradient(45deg, rgba(10,10,10,0.4), rgba(193,166,141,0.1));
          pointer-events: none;
        }

        .floating-badge {
          position: absolute;
          bottom: -1px;
          right: -1px;
          background: #1A1A1A;
          padding: 1rem 2rem;
          border-top-left-radius: 8px;
          border-top: 1px solid #C1A68D;
          border-left: 1px solid #C1A68D;
          color: #C1A68D;
          font-weight: 600;
          font-size: 1rem;
          z-index: 2;
          box-shadow: -10px -10px 30px rgba(0, 0, 0, 0.4);
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .badge-glow {
          position: absolute;
          inset: 0;
          background: radial-gradient(circle, rgba(193,166,141,0.1) 0%, transparent 70%);
          z-index: -1;
        }

        /* Stats Section */
        .stats-container {
          margin: 2rem auto 6rem;
          max-width: 1200px;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 1.5rem;
        }

        @media (max-width: 768px) {
          .stats-grid { grid-template-columns: repeat(2, 1fr); gap: 1rem; }
        }

        @media (max-width: 480px) {
          .stats-grid { grid-template-columns: 1fr; }
        }

        .stat-card {
          background: rgba(26, 26, 26, 0.6);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(193, 166, 141, 0.15);
          border-radius: 4px;
          padding: 2rem 1.5rem;
          text-align: center;
          transition: all 0.3s ease;
        }

        .stat-card:hover {
          border-color: #C1A68D;
          background: rgba(26, 26, 26, 0.8);
          transform: translateY(-3px);
        }

        .stat-number {
          font-size: 2.8rem;
          font-weight: 700;
          color: #C1A68D;
          margin-bottom: 0.5rem;
          font-family: 'Times New Roman', serif;
        }

        .stat-label {
          font-size: 0.95rem;
          color: #D4C5B0;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        /* Gallery Grid */
        .gallery-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 1.5rem;
          margin: 0 auto;
          max-width: 1400px;
        }

        .gallery-item {
          position: relative;
          border-radius: 4px;
          overflow: hidden;
          height: 400px;
          cursor: pointer;
        }

        .gallery-image-wrapper {
          position: relative;
          width: 100%;
          height: 100%;
        }

        .gallery-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.6s ease;
          filter: grayscale(20%) contrast(110%);
        }

        .gallery-item:hover .gallery-image {
          transform: scale(1.05);
          filter: grayscale(0%) contrast(110%);
        }

        .gallery-gradient {
          position: absolute;
          inset: 0;
          background: linear-gradient(to top, rgba(10, 10, 10, 0.95) 0%, rgba(10, 10, 10, 0.4) 40%, transparent 100%);
          transition: opacity 0.3s ease;
        }

        .gallery-overlay {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          padding: 2rem;
          color: white;
          transform: translateY(10px);
          transition: all 0.4s ease;
          z-index: 2;
        }

        .gallery-item:hover .gallery-overlay {
          transform: translateY(0);
        }

        .gallery-title {
          font-size: 1.2rem;
          margin-bottom: 0.5rem;
          color: #F5F0E6;
          font-weight: 500;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .gallery-line {
          height: 2px;
          width: 0;
          background: #C1A68D;
          transition: width 0.4s ease;
        }

        .gallery-item:hover .gallery-line {
          width: 40px;
        }

        /* Values Section */
        .values-section {
          max-width: 1400px;
          margin: 8rem auto 4rem;
          padding: 0;
        }

        .section-header {
          text-align: center;
          margin-bottom: 4rem;
        }

        .section-title {
          margin: 0;
          font-size: 3rem;
          font-weight: 700;
          margin-bottom: 1rem;
          text-transform: uppercase;
          letter-spacing: 2px;
          color: #F5F0E6;
        }

        .section-subtitle {
          font-size: 1.1rem;
          color: #C1A68D;
          max-width: 600px;
          margin: 0 auto;
          line-height: 1.6;
        }

        /* Values Grid */
        .values-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
        }

        .value-card {
          background: rgba(26, 26, 26, 0.4);
          backdrop-filter: blur(10px);
          border: 1px solid rgba(193, 166, 141, 0.15);
          border-radius: 4px;
          padding: 3rem 2rem;
          text-align: center;
          transition: all 0.3s ease;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .value-icon-wrapper {
          position: relative;
          margin-bottom: 1.5rem;
          padding: 1.2rem;
          background: rgba(193, 166, 141, 0.05);
          border-radius: 50%;
          border: 1px solid rgba(193, 166, 141, 0.2);
        }

        .value-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
          z-index: 2;
        }

        .value-card:hover .value-icon-wrapper {
          background: rgba(193, 166, 141, 0.1);
          border-color: #C1A68D;
        }

        .value-title {
          font-size: 1.4rem;
          color: #F5F0E6;
          margin-bottom: 1rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
        }

        .value-text {
          font-size: 1rem;
          color: #D4C5B0;
          line-height: 1.6;
          font-weight: 300;
        }

        @media (max-width: 768px) {
          .about-section { padding: 4rem 1.5rem; }
          .section-title { font-size: 2.2rem; }
          .value-card { padding: 2rem 1.5rem; }
        }
      `}</style>
    </section>
  );
};

export default AboutSection0;